import type { CreatedElement } from '../elements/element.model';
import type { ValueExpression } from '../graphpad.model';
import type { ComputedCollection } from './computed-collection';
import { parseExpression } from './expression-parser';

/**
 * ValueContext provides a unified lookup for element input params and computed values.
 *
 * Values are registered as lazy thunks (getters) so lookups are always reactive —
 * they return the current value at the time of access, not the value at registration time.
 *
 * Paths follow the pattern `{elementId}.{valueName}`, e.g. `f.a`, `f-tangent.slope`.
 */
export class ValueContext {
  private readonly values = new Map<string, () => number>();
  private readonly textValues = new Map<string, () => string>();

  /**
   * Register an element's input params and computed values.
   * Called after each element is created during init.
   */
  registerElement(elementId: string, element: CreatedElement): void {
    // Register input params as lazy lookups
    if (element.getParams) {
      const getParams = element.getParams;
      for (const key of Object.keys(getParams())) {
        this.values.set(`${elementId}.${key}`, () => getParams()[key]);
      }
    }

    // Register computed values
    if (element.getComputedValues) {
      for (const [key, thunk] of Object.entries(element.getComputedValues())) {
        this.values.set(`${elementId}.${key}`, thunk);
      }
    }

    // Register computed collections
    if (element.getComputedCollections) {
      for (const [name, collection] of Object.entries(element.getComputedCollections())) {
        this.registerCollection(elementId, name, collection);
      }
    }

    // Register expression as a text value
    if (element.getExpression) {
      this.textValues.set(`${elementId}.expression`, element.getExpression);
    }
  }

  /** Get the current value at a dotted path, or NaN if not found */
  getValue(path: string): number {
    const thunk = this.values.get(path);
    if (!thunk) {
      return NaN;
    }
    return thunk();
  }

  /** Check whether a dotted path is registered */
  has(path: string): boolean {
    return this.values.has(path);
  }

  /** Check whether a dotted path is registered as a text value */
  hasText(path: string): boolean {
    return this.textValues.has(path);
  }

  /** Get the current text value at a dotted path, or undefined if not found */
  getTextValue(path: string): string | undefined {
    return this.textValues.get(path)?.();
  }

  /**
   * Create a reactive thunk from a ValueExpression.
   * - number → constant thunk
   * - string (simple registered path) → direct lookup
   * - string (with operators) → parsed expression
   */
  createThunk(value: ValueExpression): () => number {
    if (typeof value === 'number') {
      return () => value;
    }

    // Simple path (no operators/parens) — direct lookup
    // Allow digits after dots for collection index access (e.g. f-roots.0.x)
    if (this.has(value) || /^[a-zA-Z_][a-zA-Z0-9_-]*(\.[a-zA-Z0-9_][a-zA-Z0-9_]*)*$/.test(value)) {
      return () => this.getValue(value);
    }

    // Expression with operators — parse it
    return parseExpression(value, this);
  }

  /**
   * Register a computed collection's values into the flat lookup map.
   * For unnamed collections (key ''), paths are: `{elementId}.{i}.{prop}`, `{elementId}.count`
   * For named collections, paths are: `{elementId}.{name}.{i}.{prop}`, `{elementId}.{name}.count`
   */
  private registerCollection(elementId: string, name: string, collection: ComputedCollection): void {
    const prefix = name ? `${elementId}.${name}` : elementId;
    this.values.set(`${prefix}.count`, collection.getCount);
    for (let i = 0; i < collection.maxItems; i++) {
      for (const prop of collection.properties) {
        this.values.set(`${prefix}.${i}.${prop}`, () => {
          const item = collection.getItem(i);
          return item ? item[prop]() : NaN;
        });
      }
    }
  }
}
